import { HTMLProps, forwardRef, useMemo, useState } from 'react';

import classNames from 'classnames';

import { RegisterSectionMetadata, SectionContext } from './SectionContext';

import styles from './SectionRoot.module.css';

export interface SectionRootProps
  extends Omit<HTMLProps<HTMLDivElement>, 'onChange'> {
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  registerSectionMetadata?: RegisterSectionMetadata;
  defaultExpanded?: boolean;
}

const SectionRoot = forwardRef<HTMLDivElement, SectionRootProps>(
  (props, ref) => {
    const {
      expanded: controlledExpanded,
      onExpandedChange,
      registerSectionMetadata,
      defaultExpanded = true,
      className,
      children,
      ...rest
    } = props;

    const [uncontrolledExpanded, setUncontrolledExpanded] =
      useState(defaultExpanded);
    const isControlled = controlledExpanded !== undefined;
    const expanded = isControlled ? controlledExpanded : uncontrolledExpanded;

    const contextValue = useMemo(
      () => ({
        expanded,
        onToggle: () => {
          const newExpanded = !expanded;
          if (isControlled) {
            onExpandedChange?.(newExpanded);
          } else {
            setUncontrolledExpanded(newExpanded);
          }
        },
        registerSectionMetadata,
      }),
      [expanded, isControlled, onExpandedChange, registerSectionMetadata],
    );

    return (
      <SectionContext.Provider value={contextValue}>
        <div ref={ref} className={classNames(className, styles.root)} {...rest}>
          {children}
        </div>
      </SectionContext.Provider>
    );
  },
);

SectionRoot.displayName = 'Section.Root';

export default SectionRoot;
